/**
 * Next N matches from "now".
 */

import { isPlaying, isUpcoming, hasJustPlayed } from '$utils/getMatchStats';

/**
 * Returns the timestamp (ms) for a match's ISO date, used as a sort key.
 *
 * @param {Object} match
 * @returns {number}
 */
const getMatchTime = (match) => new Date(match.isoDate).getTime();

/**
 * Checks that a match has exactly two contestants. Optionally requires that
 * each contestant has a valid `id` (used for upcoming matches where teams
 * may still be TBD).
 *
 * @param {Object} match
 * @param {{ requireIds?: boolean }} [options]
 * @returns {boolean}
 */
const hasTwoContestants = (match, { requireIds = false } = {}) => {
  const contestants = match?.matchInfo?.contestant;
  if (!Array.isArray(contestants) || contestants.length !== 2) return false;
  if (requireIds) return contestants.every((c) => c && c.id);
  return true;
};

/**
 * Filters and sorts the matches to get the live matches.
 * Live matches are sorted most-recently-started first.
 *
 * @param {Array} matches - The array of matches to filter and sort.
 * @returns {Array} - The array of live matches.
 */
const getLiveMatches = (matches) => {
  return matches
    .filter((match) => isPlaying(match) && hasTwoContestants(match))
    .sort((a, b) => getMatchTime(b) - getMatchTime(a));
};

/**
 * Filters and sorts the list of matches to get the upcoming matches.
 * Upcoming matches are sorted soonest-first.
 *
 * @param {Array} matches - The list of all matches.
 * @returns {Array} - The list of upcoming matches, sorted by date.
 */
const getUpcomingMatches = (matches) => {
  return matches
    .filter(
      (match) =>
        isUpcoming(match) && hasTwoContestants(match, { requireIds: true })
    )
    .sort((a, b) => getMatchTime(a) - getMatchTime(b));
};

/**
 * Filters and sorts the list of matches to get the matches today that just played.
 * Upcoming matches are sorted soonest-first.
 *
 * @param {Array} matches - The list of all matches.
 * @returns {Array} - The list of upcoming matches, sorted by date.
 */
const getJustPlayedMatches = (matches) => {
  return matches
    .filter((match) => {
      return (
        hasJustPlayed(match) && hasTwoContestants(match, { requireIds: true })
      );
    })
    .sort((a, b) => getMatchTime(b) - getMatchTime(a));
};

/**
 * Returns up to `count` matches, prioritising live matches followed by
 * the soonest upcoming matches.
 *
 * @param {Array} matches
 * @param {number} count
 * @returns {Array}
 */
const getLiveAndUpcomingMatches = (matches, count) => {
  const live = getLiveMatches(matches);
  if (live.length >= count) return live.slice(0, count);
  const justPlayed = getJustPlayedMatches(matches);
  // console.log('justPlayed', justPlayed)
  const upcoming = getUpcomingMatches(matches);
  return [...live, ...justPlayed, ...upcoming].slice(0, count);
};

export { getLiveMatches, getUpcomingMatches, getLiveAndUpcomingMatches };
